import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Tag, Space, Image, Typography, Divider, Row, Col, Card, Spin, Alert } from 'antd';
import { EyeOutlined, DollarOutlined, ShoppingOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { productService } from '../services/api';
import SmartImage from './SmartImage';

const { Title, Text, Paragraph } = Typography;

const ProductDetailsModal = ({ visible, onClose, productId }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && productId) {
      loadProductDetails();
    }
  }, [visible, productId]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProductById(productId);
      console.log('🔍 ProductDetailsModal - Produit chargé:', response.product);
      console.log('🔍 ProductDetailsModal - Documents:', response.product?.documents);
      setProduct(response.product);
    } catch (err) {
      console.error('Erreur lors du chargement des détails du produit:', err);
      setError('Erreur lors du chargement des détails du produit');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizeImageUrl = (raw) => {
    if (!raw) return null;
    let url = String(raw);
    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    if (url.startsWith('/uploads')) return `${url}`;
    return `${url}`;
  };

  const normalizeDocumentUrl = (raw) => {
    if (!raw) return null;
    let url = String(raw);
    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    if (url.startsWith('/uploads')) return `${url}`;
    return `${url}`;
  };

  const renderImage = (imageUrl, alt) => {
    return (
      <SmartImage
        src={imageUrl}
        alt={alt}
        width="50%"
        height={300}
        imgStyle={{ objectFit: 'contain' }}
        showFallback={true}
      />
    );
  };

  return (
    <Modal
      title={
        <Space>
          <EyeOutlined />
          <span>Détails du produit</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Chargement des détails...</div>
        </div>
      )}

      {error && (
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {product && !loading && (
        <div>
          {/* En-tête avec image principale */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              {renderImage(product.images?.main, product.name)}
            </Col>
            <Col xs={24} sm={16}>
              <Title level={3} style={{ marginBottom: 8 }}>
                {product.name}
              </Title>
              <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
                Référence: <Text code>{product.reference}</Text>
              </Text>
              <Space wrap>
                <Tag color={product.status === 'published' ? 'green' : product.status === 'draft' ? 'orange' : 'default'}>
                  {product.status === 'published' ? 'Publié' : product.status === 'draft' ? 'Brouillon' : 'Archivé'}
                </Tag>
                <Tag color={product.isVisible ? 'blue' : 'red'}>
                  {product.isVisible ? 'Visible' : 'Masqué'}
                </Tag>
                {product.isFeatured && <Tag color="gold">Mis en avant</Tag>}
              </Space>
            </Col>
          </Row>

          <Divider />

          {/* Informations générales */}
          <Descriptions title="Informations générales" bordered column={2} size="small">
            <Descriptions.Item label="Nom" span={2}>
              {product.name}
            </Descriptions.Item>
            <Descriptions.Item label="Référence">
              <Text code>{product.reference}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Slug">
              <Text code>{product.slug}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Description courte" span={2}>
              {product.shortDescription || 'Non renseignée'}
            </Descriptions.Item>
            <Descriptions.Item label="Description complète" span={2}>
              <Paragraph style={{ marginBottom: 0 }}>
                {product.description || 'Non renseignée'}
              </Paragraph>
            </Descriptions.Item>
          </Descriptions>


          {/* Sous-champs spécifiques de la catégorie */}
          {product.categories && product.categories.length > 0 && (
            <>
              <Divider />
              <Title level={4}>Caractéristiques spécifiques</Title>
              {product.categories.map((category, index) => {
                // Récupérer les champs spécifiques de cette catégorie
                const specificFields = category.specificFields || {};
                const categoryFacets = {};
                
                // Filtrer les facets du produit pour ne garder que ceux de cette catégorie
                if (product.facets) {
                  Object.keys(specificFields).forEach(fieldKey => {
                    if (product.facets[fieldKey]) {
                      categoryFacets[fieldKey] = product.facets[fieldKey];
                    }
                  });
                }
                
                // Ne pas afficher si aucun champ spécifique
                if (Object.keys(categoryFacets).length === 0) return null;
                
                return (
                  <div key={category.id || index} style={{ marginBottom: '16px' }}>
                      {product.categories?.map(category => (
              <Tag key={category.id} color="blue">
                {category.name}
              </Tag>
            )) || <Text type="secondary">Aucune catégorie</Text>}
                    <Descriptions bordered column={2} size="small" style={{ marginTop: 16 }}>
                      {Object.entries(categoryFacets).map(([key, value]) => {
                        // Ignorer les champs vides ou null
                        if (!value || (Array.isArray(value) && value.length === 0)) return null;
                        
                        // Utiliser le label de la catégorie si disponible, sinon formater le nom
                        const fieldConfig = specificFields[key];
                        const fieldName = fieldConfig?.label || key
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, str => str.toUpperCase())
                          .replace(/_/g, ' ')
                          .trim();
                        
                        // Formater la valeur
                        let displayValue = value;
                        if (Array.isArray(value)) {
                          displayValue = value.join(', ');
                        } else if (typeof value === 'string') {
                          displayValue = value;
                        }
                        
                        return (
                          <Descriptions.Item key={key} label={fieldName}>
                            <Text>{displayValue}</Text>
                          </Descriptions.Item>
                        );
                      })}
                    </Descriptions>
                  </div>
                );
              })}
            </>
          )}


          {/* Documents */}
          <Divider />
          <Title level={4}>Documents</Title>
          <Descriptions bordered column={1} size="small">
            {product.documents?.datasheet ? (
              <Descriptions.Item label="Fiche technique">
                <a 
                  href={normalizeDocumentUrl(product.documents.datasheet)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  📄 Télécharger la fiche technique
                </a>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Fiche technique">
                <Text type="secondary">Non disponible</Text>
              </Descriptions.Item>
            )}
            
            {product.documents?.manual ? (
              <Descriptions.Item label="Manuel d'utilisation">
                <a 
                  href={normalizeDocumentUrl(product.documents.manual)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  📖 Télécharger le manuel
                </a>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Manuel d'utilisation">
                <Text type="secondary">Non disponible</Text>
              </Descriptions.Item>
            )}
            
          
          </Descriptions>
          

        </div>
      )}
    </Modal>
  );
};

export default ProductDetailsModal;
